import React, { EventHandler, forwardRef, MutableRefObject, ReactNode, useCallback, useRef, useState } from "react";
import { AsyncTypeahead, Input } from "react-bootstrap-typeahead";
import classNames from "classnames";
import { Button, InputGroupAddon } from "Components/ReactstrapSlim";
import { renderAutocompleteMenu } from "Components/Autocompletes/SearchAutocomplete";
import { useSearchAutocomplete } from "Hooks";
import { CurrentLocationSearchRequest, FullTextSearchRequest, GeoSearchAutocomplete, IBaseSearchRequest, SearchAutocompleteClient, SpaceType, VenueGroupSearchAutocomplete, VenueSearchAutocomplete } from "Services/API/API";
import { getSearchUrl } from "Utils/getSearchUrl";
import { KnownSearchAutocomplete } from "Models/Search";
import { useApiClient } from "Services/API/useApiClient";
import { useCookies } from "react-cookie";

interface HeaderSearchProps {
   className?: string;
   onButtonClick: EventHandler<any>;
   buttonContent: () => ReactNode;
   typeaheadRef?: MutableRefObject<AsyncTypeahead<KnownSearchAutocomplete> | undefined>;
   ariaLabel: string;
   elementRootContainerId?: string;
   placeholder?: string;
}

export const HeaderSearch = forwardRef<HTMLFormElement, HeaderSearchProps>(({ className, onButtonClick, buttonContent, typeaheadRef, ariaLabel, elementRootContainerId, placeholder = "Find a workspace for you" }, ref) => {
   const inputRef = useRef<AsyncTypeahead<KnownSearchAutocomplete>>();
   if (typeaheadRef) {
      typeaheadRef.current = inputRef.current;
   }

   const searchAutocompleteClient = useApiClient(SearchAutocompleteClient);
   const [experimentalFullTextSearchLoading, setExperimentalFullTextSearchLoading] = useState(false);

   const [cookies] = useCookies(["UseExperimentalFullTextSearch"]);

   async function submitHandler(e?: React.SyntheticEvent) {
      e?.preventDefault();
      const inputValue = inputRef.current?.getInput().value;
      if (inputValue) {
         const request = new FullTextSearchRequest({ ...new FullTextSearchRequest() as IBaseSearchRequest, fullTextQuery: inputValue });

         if (LiquidSpaceConfig.UseExperimentalFullTextSearch || cookies.UseExperimentalFullTextSearch) {
            setExperimentalFullTextSearchLoading(true);
            try {
               const result = await searchAutocompleteClient.textToSearchQuery(inputValue);
               if (result != null) {
                  request.fullTextQuery = result.location ?? request.fullTextQuery;
                  request.minCapacity = result.minCapacity;
                  request.spaceTypes = result.spaceType ?? SpaceType.Any;
                  request.startTime = result.startDate;
               }
            }
            finally {
               setExperimentalFullTextSearchLoading(false);
            }
         }

         window.open(getSearchUrl(request), "_blank");
      } else {
         window.open(getSearchUrl(new CurrentLocationSearchRequest()), "_blank");
      }
      (inputRef.current as AsyncTypeahead<KnownSearchAutocomplete>).clear();
   }

   const { isLoading, options, handleSearch } = useSearchAutocomplete();

   const onKeyDown = useCallback(
      (e) => {
         const activeIndex = (inputRef.current?.state as any).activeIndex;
         if (e.keyCode === 13 && (activeIndex === -1 || activeIndex == null)) {
            e?.preventDefault();
            submitHandler();
         }
      },
      []
   );

   return <form onSubmit={submitHandler} className={classNames("header-search", className)} ref={ref}>
      <AsyncTypeahead<KnownSearchAutocomplete>
         ref={inputRef as any}
         filterBy={() => true}
         id={elementRootContainerId || "address"}
         inputProps={{
            className: "header-search-input rfl-input"
         }}
         className="header-search-rbt"
         isLoading={isLoading || experimentalFullTextSearchLoading}
         labelKey={option => option?.address || ""}
         minLength={3}
         onSearch={handleSearch}
         options={options}
         placeholder={placeholder}
         onChange={options => {
            const option = options[0];
            if (option instanceof GeoSearchAutocomplete || option instanceof FullTextSearchRequest || option instanceof VenueSearchAutocomplete || option instanceof VenueGroupSearchAutocomplete) {
               window.open(getSearchUrl(option), "_blank");
            }
         }}
         renderMenu={(results, menuProps) => renderAutocompleteMenu(results, menuProps)}
         onKeyDown={onKeyDown}
         renderInput={({
            inputRef,
            referenceElementRef,
            shouldSelectHint,
            ...props
         }: any, typeaheadState) => {
            return (
               <div className="input-group header-search-input-group"  >
                  <Input aria-label={placeholder}
                     {...props}
                     ref={(node) => {
                        inputRef(node);
                        referenceElementRef(node);
                     }}
                  />
                  <InputGroupAddon addonType="append">
                     <Button color="primary" className="header-search-close" aria-label={ariaLabel} onClick={onButtonClick}>
                        {buttonContent()}
                     </Button>
                  </InputGroupAddon>
               </div>
            );
         }}
      />

   </form>;
});